import { materialKeyMeasureName } from "building-generation/utils/materialKeyMeasureName";
import type { Building } from "building-types/building/buildingTypes";
import type { Casework } from "building-types/casework/caseworkTypes";
import type {
  CaseworkKeyAttributes,
  CaseworkKAM,
  CaseworkKAMOptions,
  CaseworkKeyMeasures,
} from "building-types/dataSequences/builtin/CaseworkKAM";
import type { BuildingEntityFilter } from "building-types/dataSequences/filterableBuildingEntityTypes";
import type { KeyAttributesAndMeasuresDisplayOptions } from "building-types/dataSequences/measures";
import { buildingEntityPickHandle } from "building-types/entities/buildingEntityPickHandle";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { PaletteType } from "building-types/materials/paletteType";
import type { Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { docGetTableEntity } from "doc/entity/docGetTableEntity";
import type { TableBasedDocEntityHandle } from "doc/entity/tableBasedDocEntityHandle";
import { defaultHomeSettings } from "doc/settings/defaultSettings";
import { isFixedCasework } from "doc/utils/caseworkDescriptorUtils";
import {
  keyAttributeNew,
  keyMeasureNew,
  keyAttributeOptionsNew,
  keyMeasureOptionsNew,
} from "doc/utils/dataSequenceTools";
import {
  configurableTableNew,
  categoryColumnNew,
  numberColumnNew,
  SortBy,
  stringColumnNew,
} from "lib/house/dataSequences/dataSequenceConfigurableTable";
import { removeUnderscore } from "lib/house/dataSequences/dataSequenceFormatting";

// Attributes used in procure entityHash
export const DEFINES_CASEWORK: (keyof CaseworkKeyAttributes)[] = [
  "name",
  "tag",
  "material",
  "depth",
  "width",
  "height",
];

export interface CaseworkKBMData {
  name: string;
  tag: string;
  area: number;
  width: number;
  depth: number;
  height: number;
  level: string;
  material: string;
  handle: DocEntityHandle;
}

export function makeCaseworkKAM(casework: CaseworkKBMData): CaseworkKAM {
  return {
    type: "CASEWORK",
    attributes: {
      level: keyAttributeNew(casework.level),
      name: keyAttributeNew(casework.name),
      tag: keyAttributeNew(casework.tag),
      material: keyAttributeNew(casework.material),
      entityName: keyAttributeNew(
        `CW-${casework.name}-${casework.material}-${casework.width}-${casework.height}-${casework.depth}`,
      ),
      width: keyAttributeNew(casework.width.toString(), "INCHES"),
      depth: keyAttributeNew(casework.depth.toString(), "INCHES"),
      height: keyAttributeNew(casework.height.toString(), "INCHES"),
    },
    measures: {
      area: keyMeasureNew(casework.area ?? 0, "SQ FT"),
      width: keyMeasureNew(casework.width ?? 0, "INCHES"),
      height: keyMeasureNew(casework.height ?? 0, "INCHES"),
      depth: keyMeasureNew(casework.depth ?? 0, "INCHES"),
      count: keyMeasureNew(1),
    },
    handles: [casework.handle],
  };
}
export const EMPTY_CASEWORK_KAM: CaseworkKAM = {
  type: "CASEWORK",
  attributes: {
    level: keyAttributeNew(""),
    name: keyAttributeNew(""),
    tag: keyAttributeNew(""),
    material: keyAttributeNew(""),
    // materialHash: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
    width: keyAttributeNew("0", "INCHES"),
    depth: keyAttributeNew("0", "INCHES"),
    height: keyAttributeNew("0", "INCHES"),
  },
  measures: {
    area: keyMeasureNew(0, "SQ FT"),
    width: keyMeasureNew(0, "INCHES"),
    depth: keyMeasureNew(0, "INCHES"),
    height: keyMeasureNew(0, "INCHES"),
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultCaseworkKAMOptions(): CaseworkKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      area: keyMeasureOptionsNew({ unit: "SQ FT" }),
      count: keyMeasureOptionsNew({}),
    },
  };
}
export const defaultCaseworkKAMSort = (a: CaseworkKAM, b: CaseworkKAM) => {
  return a.attributes.level.value.localeCompare(b.attributes.level.value);
};

/** @deprecated - use the Building Query API instead */
function getCaseworkFromBuilding(building: Building, filter?: BuildingEntityFilter): CaseworkKBMData[] {
  const {
    casework: caseworks,
    interiorPalette,
    exteriorPalette,
    settings: { caseworkCatalog },
  } = building;
  const caseworkTypes = (caseworkCatalog ?? defaultHomeSettings.caseworkCatalog).caseworkTypes;
  const caseworkKBMEntries: CaseworkKBMData[] = [];

  for (const levelId in building.levels) {
    const level = building.levels[levelId];

    for (const caseworkId in caseworks) {
      const c = caseworks[caseworkId];
      if (filter && !filter(c)) {
        continue;
      }
      if (c.room.level.id !== level.id) {
        continue;
      }

      const docCasework = docGetTableEntity(building.doc, c.docEntityHandle as TableBasedDocEntityHandle<"CASEWORK">);
      const caseworkDescriptor =
        docCasework && isFixedCasework(docCasework) && docCasework.catalogDescriptorId
          ? caseworkTypes[docCasework.catalogDescriptorId]
          : undefined;
      const catalogName = caseworkDescriptor?.name;

      let area = 0;
      let width = 0;
      let depth = 0;
      let height = 0;

      if (c.wallSweepExtrusions[0]) {
        assertIsNotUndefined(c.caseworkMeasures);
        assertIsNotUndefined(c.caseworkMeasures.width);
        assertIsNotUndefined(c.caseworkMeasures.depth);
        assertIsNotUndefined(c.caseworkMeasures.height);

        area = (c.caseworkMeasures.width.value * c.caseworkMeasures.depth.value) / 144;
        width = c.caseworkMeasures.width.value;
        depth = c.caseworkMeasures.depth.value;
        height = c.caseworkMeasures.height.value;
      }

      if (area + width + height === 0) {
        continue;
      }

      caseworkKBMEntries.push({
        name: catalogName ?? c.name ?? "",
        tag: c.tag?.text ?? "",
        area,
        width,
        depth,
        height,
        level: c.room.level.name,
        material: materialKeyMeasureName(
          c.paletteType === PaletteType.INTERIOR_PALETTE ? interiorPalette : exteriorPalette,
          c.material,
          c.room.type,
        ),
        handle: buildingEntityPickHandle(c),
      });
    }
  }

  return caseworkKBMEntries;
}

/** @deprecated - use the Building Query API instead */
export function buildingCaseworkKAMs(building: Building, filter?: BuildingEntityFilter): CaseworkKAM[] {
  const casework = getCaseworkFromBuilding(building, filter);
  const result: CaseworkKAM[] = [];
  for (const itemId of Object.keys(casework)) {
    result.push(makeCaseworkKAM(casework[itemId]));
  }
  return result;
}

// How do I define the measures that determine a unique mapping instance in Procure?

export const CASEWORK_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  CaseworkKeyAttributes,
  CaseworkKeyMeasures
> = {
  attributes: {
    entityName: {
      hidden: true,
    },
  },
};

export function makeCaseworkTable(casework: Table<Casework>) {
  return configurableTableNew<Casework, Casework>({
    iterables: casework,
    requirement: caseworkFilter,
    columns: [
      categoryColumnNew("Casework"),
      numberColumnNew({
        name: "Level",
        fieldFunc: (casework) => casework.room.level.floorIndex + 1,
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Notes",
        fieldFunc: (casework) => casework.planNotes[0]?.note ?? "",
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Room",
        fieldFunc: (casework) => casework.room.nameInPlan ?? casework.room.type,
        stringFormatter: removeUnderscore,
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Room ID",
        fieldFunc: (casework) => casework.room.id,
        hidden: true,
        useInDiff: false,
      }),
    ],
  });
}

function caseworkFilter(casework: Casework): Casework | null {
  if (!casework) {
    return null;
  } else {
    return casework;
  }
}
